import type { ReactNode } from 'react';

import type { IconButtonWithTooltipProps } from './IconButtonWithTooltip';
import type { TooltipAlign, TooltipSide } from '@natter-co/design-system';

type DsTooltipProps = { label: string; side: TooltipSide; align: TooltipAlign };

/**
 * Maps the legacy MUI tooltip props of `IconButtonWithTooltip` onto the Design System 2.0 `Tooltip`.
 *
 * TODO(HNS-264): temporary, like the other `toDs*Props` mappers; retiring the flag deletes it.
 *
 * `null` sends the caller back to the MUI tooltip. Cases that stay on MUI:
 *   - a `title` that is not a string - the DS label is a string on purpose, so nodes never become
 *     a popover by accident;
 *   - any tooltip prop other than `placement`, `display` and `arrow` (`slotProps`, ...) - the DS
 *     tooltip has no equivalent, and accepting them would drop them silently. `display` is a
 *     layout prop for the MUI wrapper span, which the DS tooltip does not render.
 *
 * MUI's `-start` / `-end` follow the positioning engine's convention, which is exactly what `align`
 * uses - unlike Figma's `TopLeft` / `TopRight` names, which are mirrored.
 */
export const toDsTooltipProps = ({
  title,
  TooltipProps,
}: Pick<IconButtonWithTooltipProps, 'TooltipProps' | 'disabled'> & {
  title: ReactNode;
}): DsTooltipProps | null => {
  if (typeof title !== 'string') return null;

  // The DS dot is fixed; legacy arrow and wrapper display preferences do not change it.
  if (Object.keys(TooltipProps ?? {}).some((key) => !['placement', 'display', 'arrow'].includes(key))) return null;
  const placement = TooltipProps?.placement ?? 'top';

  const [side, align = 'center'] = placement.split('-');
  return { label: title, side: side as TooltipSide, align: align as TooltipAlign };
};
